/**
 * The pure half of the combobox (components/Combobox.tsx): which options a typed
 * text shows, and whether it offers to add the text as a new value. Matching
 * ignores case and extra spacing, the same rule the server applies when it
 * canonicalizes a saved value (analysis/taxonomy-values.ts), so the picker never
 * offers "Add 'vcore'" while "VCORE" exists. Unit-tested in combobox.test.ts.
 */

/** The comparison key: trimmed, inner whitespace collapsed, lower-cased. */
export function optionKey(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** The most options the list shows at once; typing narrows the rest. */
export const COMBOBOX_LIMIT = 50;

export interface ComboboxView {
  /** The options to list, in their given order, at most `COMBOBOX_LIMIT`. */
  shown: string[];
  /** How many more matched than are shown. */
  hidden: number;
  /** The existing option that equals the text ignoring case, if any. */
  exact: string | null;
  /** The cleaned text to offer as a new value, or null (blank, or it already exists). */
  create: string | null;
}

/**
 * The list for a query. `query` null means the owner has not typed since the list
 * opened, so every option shows; otherwise the options containing the query
 * (ignoring case) show, those starting with it first. `canCreate` enables the
 * "add as new" offer for a query no option equals.
 */
export function comboboxView(
  options: readonly string[],
  query: string | null,
  canCreate: boolean,
  limit = COMBOBOX_LIMIT,
): ComboboxView {
  const key = query === null ? '' : optionKey(query);
  const exact = key === '' ? null : (options.find((o) => optionKey(o) === key) ?? null);
  let matched: string[];
  if (key === '') {
    matched = [...options];
  } else {
    const starts: string[] = [];
    const contains: string[] = [];
    for (const option of options) {
      const k = optionKey(option);
      if (k.startsWith(key)) starts.push(option);
      else if (k.includes(key)) contains.push(option);
    }
    matched = [...starts, ...contains];
  }
  const cleaned = query === null ? '' : query.replace(/\s+/g, ' ').trim();
  return {
    shown: matched.slice(0, limit),
    hidden: Math.max(0, matched.length - limit),
    exact,
    create: canCreate && cleaned !== '' && exact === null ? cleaned : null,
  };
}
